// @ts-nocheck
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAtomValue } from 'jotai';

import { useTranslation } from 'react-i18next';
import { Container, Row, Col } from 'react-bootstrap';

import { HeaderS } from '@shared/ui/header';
import { Footer } from '@shared/ui/footer';
import { changeLanguage } from '@entities/language/store';
import {
  formResetComplete,
  dcbUnsubscribe,
} from '@features/card-subscription/model/subscription-payment-flow';
import { Helpers } from '@shared/utils/Helpers';
import { setHtmlDirection } from '@shared/utils';
import { getFinalNumberFromDialCode, setTheme } from '@shared/utils/general';
import { APP_THEMES } from '@shared/config/constants/app';
import { TelInput } from '@features/phone-subscription';
import parsePhoneNumber from 'libphonenumber-js/mobile';
import { isJO, isMA } from '@entities/country';
import { Banner } from '@features/phone-subscription';
import { useParams } from 'react-router-dom';
import { AR_lang, EN_lang } from '@entities/language';
import Alert from '@shared/ui/alert/ui/Alert';
import { unsubBannerConfig } from '@shared/config/constants/Banners';
import { subscriptionAtom } from '@shared/store/slice-atoms';

const DCBUnsubscribe = ({
  isArabic = false,
  languageConfig = { availableLanguages: [EN_lang, AR_lang] },
}) => {
  const { t, i18n } = useTranslation();
  const formStartRef = useRef(null);
  const howItWorksRef = useRef(null);

  const { token } = useParams();

  const { btnLoading, needToResetForm } = useAtomValue(subscriptionAtom);

  const [phone, setPhone] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('ae');
  const [countryCode, setCountryCode] = useState('+971');

  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isArabic) {
      let lang = { locale: 'ar', displayName: 'العربية', direction: 'rtl' };
      i18n.changeLanguage(lang.locale);
      changeLanguage(lang);
      setHtmlDirection('rtl');
    }

    setTheme(APP_THEMES.WHITE);
  }, []);

  useEffect(() => {
    if (needToResetForm) {
      setPhone('');
      formResetComplete();
    }
  }, [needToResetForm]);

  const handleSubmitUnsubscribe = (e) => {
    e.preventDefault();
    if (errors.phone || phone === '') {
      setErrorMessage(t('error_phone'));
      return;
    } else {
      setErrorMessage('');
    }
    dcbUnsubscribe({
        mobile: phone,
        mobileCountryCode: countryCode,
        userToken: token || '',
        forceIgnoreVuz: true,
        forceIgnoreAggregator: true,
      });
  };

  const handlePhoneChange = (isValid, inputAsIs, iso2, international) => {
    let lettersRegExp = /[a-zA-Z+]/g; //:\'\".,-:;

    if (lettersRegExp.test(inputAsIs)) {
      return;
    }

    if (inputAsIs.includes('.') || inputAsIs.includes(',')) {
      return;
    }

    if (!iso2) return;
    if (iso2 && !iso2.iso2) return;
    const dialCode = iso2.dialCode;
    const isoCode = iso2.iso2.toUpperCase();

    let finalNumber = getFinalNumberFromDialCode(inputAsIs, dialCode);

    let dialCode2 = '972';

    if (dialCode === '970') {
      finalNumber = getFinalNumberFromDialCode(finalNumber, dialCode2);
    }

    const phoneNumber = parsePhoneNumber(finalNumber, isoCode);
    const isPhoneNumberValidForJO =
      Helpers.isPhoneNumberValidSpecialCases(inputAsIs) && isJO(selectedCountry);
    const isPhoneNumberValidForMA =
      Helpers.isPhoneNumberValidSpecialCases(inputAsIs) && isMA(selectedCountry);

    let newFinal = '';
    setPhone(inputAsIs);
    if (finalNumber.includes(`+${dialCode}`)) {
      newFinal = finalNumber.split(`+${dialCode}`)[1];
    } else if (finalNumber.includes(`+${dialCode2}`)) {
      newFinal = finalNumber.split(`+${dialCode2}`)[1];
    } else if (isPhoneNumberValidForJO) {
      newFinal = inputAsIs;
    } else if (isPhoneNumberValidForMA) {
      newFinal = inputAsIs;
    } else {
      newFinal = finalNumber;
    }

    setErrorMessage('');
    setPhone(newFinal);
    setCountryCode(dialCode);

    if (
      (phoneNumber && phoneNumber?.isValid()) ||
      isPhoneNumberValidForJO ||
      isPhoneNumberValidForMA
    ) {
      let newErrors = { ...errors };
      delete newErrors.phone;
      setErrors(newErrors);
    } else {
      setErrors({ ...errors, phone: t('error_phone') });
    }
  };

  const preferredCountries = useMemo(() => {
    if (selectedCountry) {
      return [selectedCountry];
    }

    if (!selectedCountry) {
      return ['ae'];
    }

    if (selectedCountry === 'UK') {
      return ['gb'];
    }

    return ['ae'];
  }, [selectedCountry]);

  return (
    <article className="landing-article">
      <HeaderS
        formStartRef={formStartRef}
        howItWorksRef={howItWorksRef}
        languageConfig={languageConfig}
        isCC
        isUnsubPage
      />
      <Banner bannerConfig={unsubBannerConfig} />
      <Container className="container-sm">
        <section className="landing-section-4">
          <form onSubmit={handleSubmitUnsubscribe}>
            <Row className="mt-3">
              <Col>
                <p className="teleport-subtitle">{t('enter_mobile_to_unsubscribe')}</p>
              </Col>
            </Row>

            <Row className="mt-2 mb-2">
              <Col>
                <TelInput
                  phone={phone}
                  handlePhoneChange={handlePhoneChange}
                  preferredCountries={preferredCountries}
                  showCountryDropdown={true}
                  setSelectedCountry={setSelectedCountry}
                  hideCountryFlag={false}
                  placeholder="XXXXXXXXX"
                />
              </Col>
            </Row>

            <div className="text-center mb-3">
              <Alert />
              {errorMessage && <p className="error-message">{errorMessage}</p>}
              {errors.phone && phone && <p className="error-message">{errors.phone}</p>}

              <button
                id="unsubscribe-button"
                type="submit"
                className="vuz-btn-1 large lh-07 mt-2 join-btn-confirm"
              >
                <span className="subscribe-btn-txt">
                  {btnLoading ? (
                    <>
                      <i className="fas fa-circle-notch fa-spin sm-icon-margin-1" />
                      {t('processing')}
                    </>
                  ) : (
                    t('unsubscribe')
                  )}
                </span>
              </button>
              <br />
              <br />
            </div>
          </form>
          <Footer
            isWhite
            hideFeatures
            isOTP
            vipFeaturesConfig={{ hide: true }}
            followUsConfig={{ hide: true }}
            moreAboutUsConfig={{ hide: true }}
            aboutUsConfig={{ hide: true }}
          />
        </section>
      </Container>
    </article>
  );
};

export { DCBUnsubscribe };
